import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listarClientesTodos,
  cadastrarClienteZenMobile,
  atualizarClienteZenMobile,
  obterClienteZenMobile,
} from "../services/api";
import { Search, Save } from "lucide-react";

const FormularioZenMobile = ({ token }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id !== undefined;

  const [clientes, setClientes] = useState([]);
  const [clientesOriginais, setClientesOriginais] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState("");
  const [qtdDispositivos, setQtdDispositivos] = useState(1);
  const [codigoInstalacao, setCodigoInstalacao] = useState("");
  const [descricao, setDescricao] = useState("");

  const [busca, setBusca] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingLicenca, setLoadingLicenca] = useState(false);
  const [error, setError] = useState(null);

  // ====== clientes (igual ZenTag) ======
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const resp = await listarClientesTodos(token, {
          pageSize: 2000,
          sortBy: "razaosocial",
        });

        // normaliza p/ array (evita clientes.map quebrar)
        const data = resp?.data ?? resp;
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.Items)
          ? data.Items
          : [];

        setClientes(arr);
        setClientesOriginais(arr);
      } catch (err) {
        setError("Erro ao carregar clientes.");
        console.error("Erro ao carregar clientes:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchClientes();
  }, [token]);

  // ====== licença (edit) ======
  useEffect(() => {
    if (!isEditMode) return;

    const fetchLicenca = async () => {
      setLoadingLicenca(true);
      try {
        const response = await obterClienteZenMobile(id, token);
        const lic = response?.data;

        setSelectedClientId(String(lic?.clienteId ?? ""));
        setQtdDispositivos(Number(lic?.qtdDispositivos ?? 1));
        setCodigoInstalacao(String(lic?.codigoInstalacao ?? ""));
        setDescricao(lic?.descricao ?? "");
      } catch (err) {
        setError("Erro ao carregar dados do ZenMobile.");
        console.error("Erro ao carregar dados do ZenMobile:", err);
      } finally {
        setLoadingLicenca(false);
      }
    };

    fetchLicenca();
  }, [id, isEditMode, token]);

  // ====== cliente selecionado (igual ZenTag) ======
  const clienteSelecionado = useMemo(() => {
    const idNum = Number(selectedClientId);
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];
    return base.find((c) => c.id === idNum) || null;
  }, [selectedClientId, clientesOriginais]);

  // ====== filtro busca (igual ZenTag) ======
  const handleBuscar = (e) => {
    const termo = e.target.value;
    setBusca(termo);

    const t = termo.toLowerCase();
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];

    const filtered = base.filter(
      (c) =>
        (c.cnpj || "").toLowerCase().includes(t) ||
        (c.razaoSocial || "").toLowerCase().includes(t) ||
        (c.nomeFantasia || "").toLowerCase().includes(t)
    );

    setClientes(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedClientId) {
      alert("Por favor, selecione um cliente.");
      return;
    }

    if (!clienteSelecionado) {
      alert("Cliente selecionado não encontrado.");
      return;
    }

    const payload = {
      clienteId: Number(selectedClientId),
      revendaId: clienteSelecionado?.revendaId ?? 0,
      nomeFantasia: clienteSelecionado?.nomeFantasia ?? "",
      descricao: descricao ?? "",
      qtdDispositivos: Number(qtdDispositivos),
      codigoInstalacao: codigoInstalacao ? Number(codigoInstalacao) : 0,
      ativo: true,
      mensagemBloqueio: "",
    };

    setLoadingLicenca(true);
    try {
      if (isEditMode) {
        await atualizarClienteZenMobile(id, payload, token);
        alert("ZenMobile atualizado com sucesso!");
      } else {
        await cadastrarClienteZenMobile(payload, token);
        alert("ZenMobile cadastrado com sucesso!");
      }
      navigate("/licenciamento/zenmobile/list");
    } catch (err) {
      const data = err?.response?.data;

      if (typeof data === "string") {
        alert(data);
      } else {
        const message =
          data?.message ||
          (data?.errors && Object.values(data.errors).flat().join("\n")) ||
          err.message ||
          "Erro ao salvar ZenMobile.";
        alert(message);
      }
      console.error(err);
    } finally {
      setLoadingLicenca(false);
    }
  };

  if (loading)
    return (
      <div className="p-6 text-center text-gray-600 dark:text-slate-200">
        Carregando clientes...
      </div>
    );

  if (error)
    return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        {/* header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {isEditMode ? "Editar ZenMobile" : "Nova Licença ZenMobile"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            {isEditMode
              ? "Edite os dados da licença (cliente travado)."
              : "Selecione um cliente existente e informe os dados da licença."}
          </p>
        </div>

        {/* seletor cliente */}
        <div className="zen-card rounded-2xl p-4 mb-6 dark:bg-white/5 dark:border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar cliente por CNPJ / Razão / Nome Fantasia"
                value={busca}
                onChange={handleBuscar}
                disabled={isEditMode}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  disabled:opacity-60 disabled:cursor-not-allowed
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>

            <select
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                disabled:opacity-60 disabled:cursor-not-allowed
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              required
              disabled={isEditMode}
            >
              <option value="">-- Selecione um cliente --</option>
              {Array.isArray(clientes) &&
                clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial} ({c.cnpj})
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Descrição
                </label>
                <input
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  placeholder="ex: Unidade Morumbi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Quantidade de Dispositivos
                </label>
                <input
                  type="number"
                  min="1"
                  value={qtdDispositivos}
                  onChange={(e) => setQtdDispositivos(e.target.value)}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Código de Instalação
                </label>
                <input
                  value={codigoInstalacao}
                  onChange={(e) => setCodigoInstalacao(e.target.value)}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  placeholder="ex: 123456"
                />
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loadingLicenca}
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              <Save size={18} />
              {loadingLicenca
                ? "Salvando..."
                : isEditMode
                ? "Salvar Alterações"
                : "Cadastrar no ZenMobile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioZenMobile;
